// Help Scout Beacon: the live support chat the legacy customer pages loaded
// (app/views/shared/_helpscout_beacon.html.erb), with the same public Beacon
// ids. The Post a Project screens get their own Beacon, as the legacy job
// posting pages did (app/views/job_postings/_job_posting_beacon.html.erb).
export const CUSTOMER_BEACON = "1cf28780-520f-4424-8381-08b1faaeee51"
export const POSTING_BEACON = "7672c206-b6e2-40f4-8cf4-9406751d6c57"

const SCRIPT_SRC = "https://beacon-v2.helpscout.net"

// The shell persists across Inertia visits, so the script loads once per full
// page load; a later visit that needs the other Beacon swaps it in place.
let scriptStatus = "idle" // idle | loading | ready | failed
let currentId = null
// A click made while the script is still loading: told if the load then fails.
let pendingBlocked = null

// Help Scout's embed queue: calls made before the script arrives are replayed
// by the real Beacon when it loads.
function ensureQueue() {
  if (window.Beacon) return
  const queue = (method, options, data) => queue.readyQueue.push({ method, options, data })
  queue.readyQueue = []
  window.Beacon = queue
}

// identity ({ name, email, signature }, signed on the server) is sent only to
// the customer Beacon: it is the one with a Help Scout Secure Mode key, and an
// identified customer sees their past support conversations there.
export function loadHelpBeacon(id, identity) {
  if (typeof window === "undefined") return
  ensureQueue()

  if (scriptStatus === "idle") {
    scriptStatus = "loading"
    const script = document.createElement("script")
    script.async = true
    script.src = SCRIPT_SRC
    script.onload = () => {
      scriptStatus = "ready"
      pendingBlocked = null
    }
    script.onerror = () => {
      scriptStatus = "failed"
      pendingBlocked?.()
      pendingBlocked = null
    }
    document.head.appendChild(script)
  }

  if (currentId === id) return
  if (currentId) window.Beacon("destroy")
  window.Beacon("init", id)
  // No floating bubble: it covered the page's bottom-right corner. The side
  // nav's Support › Get help item opens the Beacon instead.
  window.Beacon("config", { display: { style: "manual" } })
  if (identity && id === CUSTOMER_BEACON) window.Beacon("identify", identity)
  currentId = id
}

// Help Scout keeps each Beacon's identity, device id and conversation state in
// localStorage under keys starting with this prefix.
const STORAGE_PREFIX = "persist:hs-beacon"

// Sign-out on a shared computer, so the next person to use it never sees this
// customer's conversations. The stored identity is cleared synchronously,
// before the page leaves, whether or not a Beacon loaded on this page (an
// earlier page may have identified the customer). The Beacon's own logout,
// which also ends a live chat, is best effort: it may still be queued when the
// sign-out navigation unloads the page.
export function logoutHelpBeacon() {
  if (typeof window === "undefined") return
  try {
    const keys = Object.keys(window.localStorage).filter((key) => key.startsWith(STORAGE_PREFIX))
    keys.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Storage blocked or unavailable: nothing was persisted to clear.
  }

  if (!currentId) return
  loadHelpBeacon(CUSTOMER_BEACON)
  window.Beacon("logout", { endActiveChat: true })
}

// Opens the Beacon on its front screen: help article search plus the ask
// options (chat or message). onBlocked runs when the script was blocked (ad blockers,
// VPNs and privacy extensions commonly block it), now or, for a click made
// while it is still loading, when that load fails.
export function openHelp(onBlocked) {
  if (scriptStatus === "failed" || typeof window === "undefined" || !window.Beacon) {
    onBlocked?.()
    return
  }
  if (scriptStatus === "loading") pendingBlocked = onBlocked
  window.Beacon("open")
  window.Beacon("navigate", "/")
}
